import React, { useCallback, useState } from 'react';
import UserDetailPreview from './UserDetailPreview';
import * as styles from './UserDetailPreviewPopOver.module.scss';
import PopOverArrow from '../components/PopOverArrow';
import PopOverPrimitive from '../components/PopOverPrimitive';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import contentPreviewPopOverSize from '../utils/popover/contentPreviewPopOverSize';

const SCREEN_PADDING = 12;

interface UserDetailPreviewPopOverProps {
  userId: string;
  children: React.ReactNode;
  placement?: React.ComponentProps<typeof PopOverPrimitive>['placement'];
  slowHoverDelay?: boolean;
}

const UserDetailPreviewPopOver = ({
  userId,
  children,
  placement,
  slowHoverDelay,
}: UserDetailPreviewPopOverProps) => {
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  const isHidden = minHeight === null;

  const getAdditionalMiddleware = useCallback<
    NonNullable<
      React.ComponentProps<typeof PopOverPrimitive>['getAdditionalMiddleware']
    >
  >(
    (options) => [
      contentPreviewPopOverSize({ ...options, fullWidth: isMobile }),
    ],
    [isMobile],
  );

  return (
    <PopOverPrimitive
      hoverGroupId="userDetailPreviewPopOver"
      screenPadding={SCREEN_PADDING}
      placement={placement}
      enabledForTouch
      hideWhenReferenceHidden
      getAdditionalMiddleware={getAdditionalMiddleware}
      hoverDelay={slowHoverDelay ? 'slow' : 'fast'}
      trigger="clickOrHover"
      offset={10}
      hidden={isHidden}
    >
      <PopOverPrimitive.Trigger asChild>{children}</PopOverPrimitive.Trigger>

      <PopOverPrimitive.Floating
        minHeight={minHeight ?? undefined}
        className={styles.floatingContainer}
      >
        <PopOverPrimitive.Content className={styles.content}>
          <UserDetailPreview userId={userId} setMinHeight={setMinHeight} />
        </PopOverPrimitive.Content>
        {!isHidden && <PopOverArrow />}
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export default UserDetailPreviewPopOver;
